import React, { useState, useCallback, useRef } from "react";
import { View, Text, StyleSheet } from "react-native";
import Svg, { Path, G, Circle } from "react-native-svg";
import { EDColors } from "../constants/Colors";

/** Get slice index from position (locationX, locationY). Pie starts at top (-PI/2), goes clockwise. Returns -1 if outside circle. */
function getSliceIndexAt(x, y, size, data, total) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;
  const dx = x - cx;
  const dy = y - cy;
  const distSq = dx * dx + dy * dy;
  if (distSq > r * r) return -1;
  const angle = Math.atan2(dy, dx);
  const normalized = (angle + Math.PI / 2 + 2 * Math.PI) % (2 * Math.PI);
  let cum = 0;
  for (let i = 0; i < data.length; i++) {
    const ratio = (data[i].value || 0) / total;
    const end = cum + ratio * 2 * Math.PI;
    if (normalized >= cum && normalized < end) return i;
    cum = end;
  }
  return -1;
}

/**
 * Reusable pie chart component.
 * @param {Object} props
 * @param {Array<{ label: string, value: number, color: string }>} props.data - Slice label, value, and color
 * @param {number} [props.size=120] - Chart width/height
 * @param {string} [props.emptyText='No data'] - Text when total is 0
 * @param {boolean} [props.showTooltip=false] - Show tooltip on slice hover/press with percentage
 */
function PieChart({ data = [], size = 120, emptyText = "No data", showTooltip = false }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const wrapperRef = useRef(null);
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 4;

  const handlePointer = useCallback(
    (evt) => {
      if (!showTooltip || total === 0) return;
      const native = evt?.nativeEvent ?? evt;
      let x = native?.locationX ?? native?.offsetX;
      let y = native?.locationY ?? native?.offsetY;
      if (x == null && typeof document !== "undefined" && wrapperRef.current?.getBoundingClientRect) {
        const rect = wrapperRef.current.getBoundingClientRect();
        x = (native?.clientX ?? native?.pageX ?? 0) - rect.left;
        y = (native?.clientY ?? native?.pageY ?? 0) - rect.top;
      }
      x = x ?? 0;
      y = y ?? 0;
      const idx = getSliceIndexAt(x, y, size, data, total);
      setActiveIndex(idx >= 0 ? idx : null);
    },
    [showTooltip, data, total, size]
  );

  const handlePointerLeave = useCallback(() => setActiveIndex(null), []);

  if (total === 0) {
    return (
      <View style={[chartStyles.container, { width: size, height: size }]}>
        <Svg width={size} height={size}>
          <Circle
            cx={cx}
            cy={cy}
            r={r}
            fill={EDColors.weeklightgrey}
            stroke="#eee"
            strokeWidth={1}
          />
        </Svg>
        <Text style={[chartStyles.emptyText, { position: "absolute" }]}>
          {emptyText}
        </Text>
      </View>
    );
  }

  const slicesWithValue = data.filter((d) => (d.value || 0) > 0);
  if (slicesWithValue.length === 1) {
    const slice = slicesWithValue[0];
    const sliceIndex = data.indexOf(slice);
    const showTip = showTooltip && activeIndex === sliceIndex;
    return (
      <View ref={wrapperRef} style={[chartStyles.wrapper, { width: size, height: size }]}>
        <Svg width={size} height={size}>
          <Circle
            cx={cx}
            cy={cy}
            r={r}
            fill={slice.color}
            stroke="#fff"
            strokeWidth={1}
          />
        </Svg>
        {showTooltip && (
          <View
            style={StyleSheet.absoluteFill}
            onStartShouldSetResponder={() => true}
            onResponderGrant={handlePointer}
            onResponderRelease={handlePointerLeave}
            onMouseMove={handlePointer}
            onMouseLeave={handlePointerLeave}
          />
        )}
        {showTip && (
          <View
            style={[
              chartStyles.tooltip,
              {
                left: cx - 45,
                top: cy - r - 36,
              },
            ]}
            pointerEvents="none"
          >
            <Text style={chartStyles.tooltipText}>
              {slice.label}: {total > 0 ? ((slice.value / total) * 100).toFixed(1) : 0}%
            </Text>
          </View>
        )}
      </View>
    );
  }

  let startAngle = -Math.PI / 2;
  const slices = data.map((d) => {
    const value = d.value || 0;
    const ratio = value / total;
    const endAngle = startAngle + ratio * 2 * Math.PI;
    const midAngle = startAngle + ratio * Math.PI;
    const centerX = cx + r * 0.6 * Math.cos(midAngle);
    const centerY = cy + r * 0.6 * Math.sin(midAngle);
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = ratio > 0.5 ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    startAngle = endAngle;
    return { path, color: d.color, centerX, centerY, label: d.label, value: d.value };
  });

  const activeSlice = activeIndex != null ? slices[activeIndex] : null;

  return (
    <View ref={wrapperRef} style={[chartStyles.wrapper, { width: size, height: size }]}>
      <Svg width={size} height={size}>
        <G>
          {slices.map((slice, i) => (
            <Path
              key={i}
              d={slice.path}
              fill={slice.color}
              stroke="#fff"
              strokeWidth={1}
            />
          ))}
        </G>
      </Svg>
      {showTooltip && (
        <View
          style={chartStyles.overlay}
          onStartShouldSetResponder={() => true}
          onResponderGrant={handlePointer}
          onResponderRelease={handlePointerLeave}
          onMouseMove={handlePointer}
          onMouseLeave={handlePointerLeave}
        />
      )}
      {showTooltip && activeSlice && (
        <View
          style={[
            chartStyles.tooltip,
            {
              left: Math.max(4, Math.min(size - 94, activeSlice.centerX - 45)),
              top: Math.max(4, activeSlice.centerY - 36),
            },
          ]}
          pointerEvents="none"
        >
          <Text style={chartStyles.tooltipText}>
            {activeSlice.label}: {total > 0 ? ((activeSlice.value / total) * 100).toFixed(1) : 0}%
          </Text>
        </View>
      )}
    </View>
  );
}

const chartStyles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
  },
  wrapper: {
    position: "relative",
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    cursor: "pointer",
  },
  emptyText: {
    fontSize: 12,
    color: EDColors.grey,
  },
  tooltip: {
    position: "absolute",
    backgroundColor: EDColors.topbar,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    zIndex: 10,
  },
  tooltipText: {
    fontSize: 12,
    color: "#fff",
  },
});

export default PieChart;
